"use client";

import React, { useContext, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useLocation } from "react-router-dom";
import { AppStateContext } from "@/components/context/AppContext";
import MarketingLogo from "./MarketingLogo";
import { candidatePracticeUrl, useCandidateCta } from "@/lib/candidateCta";
import { openCookieSettings } from "@/components/analytics/CookieConsent";
import { isSelfServe, useVisitorRegion } from "@/lib/visitorRegion";
import LeadSlideIn from "@/components/lead-magnets/lead-slide-in";
import NativeLanguageBar from "./NativeLanguageBar";
import { NATIVE_BY_PATH, NATIVE_LANDINGS } from "@/lib/native/aiiaLanding";
import { parseNativeCandidatePath } from "@/lib/native/candidatePages";

import "./MarketingCore.css";
import "./MarketingContent.css";

// Sales-led regions (India, Pakistan, Bangladesh) book a demo; the region is
// also the fallback while the visitor's region is unknown. Self-serve regions
// start free and see pricing.
const DEMO_URL = "/book-a-demo";
const START_FREE_URL = "/auth/register?type=hr";
const SIGNIN_URL = "/auth/login?type=hr";

const MAIN_LANDMARK_ROUTES = new Set([
  "/pricing",
  "/contact",
  "/privacy-policy",
  "/terms-of-use",
  "/refund-policy",
  "/recruiters/ai-interview-bot",
  "/connect",
]);

const productLinks = [
  ["For recruiters", "/recruiters"],
  ["AI Interviewer", "/recruiters/ai-interviewer"],
  ["AI Video Interviews", "/recruiters/ai-video-interview-software"],
  ["Interview Highlight Clips", "/recruiters/interview-highlight-clips"],
  ["Call to Submittal", "/recruiters/call-to-submittal"],
  ["Resume Screening", "/recruiters/ai-resume-screening"],
  ["Agentic Resume Search", "/recruiters/agentic-resume-search"],
  ["Career Page Builder", "/recruiters/career-page-builder"],
  ["Candidate Location Check", "/recruiters/candidate-location-check"],
  ["Joining Readiness", "/recruiters/joining-readiness"],
  ["AI Phone Screening", "/recruiters/ai-phone-screening"],
];

const resourcesLinks = [
  ["Blog", "/blog"],
  ["Interview questions", "/interview-questions"],
  ["Compare", "/compare"],
  ["Tools", "/tools"],
  ["Connect", "/connect"],
];

const companyLinks = [
  ["About", "/about"],
  ["Contact", "/contact"],
];

// The English pages link to the five native pages (/de /fr /es /sv /it), each in its own
// language. Plain links, never a redirect: Google asks for separate URLs per language.
const languageLinks = NATIVE_LANDINGS.map((l) => [
  l.nativeName,
  l.path,
  { lang: l.code, hrefLang: l.hreflang, "data-analytics-id": `nav_language_${l.code}` },
]);

function GlobeIcon() {
  return (
    <svg className="nav-globe" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.6 2.7 3.9 5.7 3.9 9s-1.3 6.3-3.9 9c-2.6-2.7-3.9-5.7-3.9-9S9.4 5.7 12 3z" />
    </svg>
  );
}

function routeClass(pathname) {
  if (pathname === "/") return "marketing-home";
  if (pathname === "/blog" || pathname.startsWith("/blog/")) return "marketing-blog";
  if (/^\/(interview-questions|templates|compare|industries|integrations|security|languages)(\/|$)/.test(pathname)) return "marketing-blog";
  if (pathname === "/tools" || pathname.startsWith("/tools/")) return "marketing-tools";
  if (pathname === "/pricing") return "marketing-pricing";
  if (pathname === "/about") return "marketing-about";
  if (pathname === "/contact") return "marketing-contact";
  if (pathname === "/book-a-demo") return "marketing-book";
  if (pathname === "/privacy-policy" || pathname === "/terms-of-use" || pathname === "/refund-policy") return "marketing-legal";
  if (pathname.startsWith("/features/") || pathname === "/candidates") return "marketing-candidate";
  if (pathname === "/connect") return "marketing-connect";
  if (pathname === "/recruiters" || pathname.startsWith("/recruiters/")) return "marketing-product";
  return "";
}

function NavMenu({ id, label, links, openMenu, setOpenMenu, icon = null }) {
  const isOpen = openMenu === label;
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const triggerId = `${id}-trigger`;
  const dropdownId = `${id}-dropdown`;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setOpenMenu(null);
      buttonRef.current?.focus();
    };
    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpenMenu(null);
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isOpen, setOpenMenu]);

  return (
    <div ref={menuRef} className={`nav-menu${isOpen ? " is-open" : ""}`}>
      <button
        ref={buttonRef}
        id={triggerId}
        className="nav-menu-button"
        type="button"
        data-analytics-id={`nav_${id.replace(/-menu$/, "")}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={dropdownId}
        onClick={() => setOpenMenu(isOpen ? null : label)}
      >
        {icon ? <span className="nav-menu-label">{icon}{label}</span> : label}<span className="nav-chevron" aria-hidden="true">⌄</span>
      </button>
      <div className="nav-dropdown" id={dropdownId} aria-labelledby={triggerId}>
        {links.map(([name, href, attrs]) => (
          <Link key={href} href={href} onClick={() => setOpenMenu(null)} {...attrs}>{name}</Link>
        ))}
      </div>
    </div>
  );
}

function MarketingHeader({ pathname }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const menuButtonRef = useRef(null);
  // The candidate pages (practice features, the candidate hub, candidate blog
  // posts) send their account CTAs to the candidate product; every other route
  // keeps the recruiter destinations.
  const candidateCta = useCandidateCta(pathname);
  const selfServe = isSelfServe(useVisitorRegion()) && !candidateCta;
  // A signed-in recruiter gets Dashboard, not Sign in: Sign in only forwarded
  // them to the dashboard and made the site look signed out.
  const { userProfile } = useContext(AppStateContext) || {};
  const signedIn = Boolean(userProfile) && userProfile.role !== "candidate";

  const closeMenus = (restoreFocus = false) => {
    setMobileOpen(false);
    setOpenMenu(null);
    if (restoreFocus) menuButtonRef.current?.focus();
  };

  useEffect(() => {
    const updateHeader = () => setScrolled(window.scrollY > 24);
    const desktop = window.matchMedia("(min-width: 960px)");
    const handleDesktop = (event) => {
      if (event.matches) closeMenus();
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (mobileOpen) {
        closeMenus(true);
      }
    };

    updateHeader();
    window.addEventListener("scroll", updateHeader, { passive: true });
    document.addEventListener("keydown", handleKeyDown);
    desktop.addEventListener("change", handleDesktop);
    return () => {
      window.removeEventListener("scroll", updateHeader);
      document.removeEventListener("keydown", handleKeyDown);
      desktop.removeEventListener("change", handleDesktop);
    };
  }, [mobileOpen, openMenu]);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [pathname]);

  return (
    <header className={`site-header${scrolled ? " is-scrolled" : ""}${mobileOpen ? " menu-open" : ""}`}>
      <div className="header-inner">
        <Link className="wordmark" href="/" aria-label="AIIA home" onClick={() => closeMenus()}>
          <MarketingLogo />
        </Link>
        <button
          ref={menuButtonRef}
          id="aiia-site-nav-trigger"
          className="menu-button"
          data-analytics-id="nav_menu"
          type="button"
          aria-expanded={mobileOpen}
          aria-controls="aiia-site-nav"
          onClick={() => {
            setMobileOpen((open) => !open);
            setOpenMenu(null);
          }}
        >
          <span className="menu-label">Menu</span>
          <span className="menu-lines" aria-hidden="true"><span></span><span></span></span>
        </button>
        <nav className="site-nav" id="aiia-site-nav" aria-label="Main navigation">
          <div className="nav-links">
            <NavMenu id="product-menu" label="Product" links={productLinks} openMenu={openMenu} setOpenMenu={setOpenMenu} />
            <NavMenu id="resources-menu" label="Resources" links={resourcesLinks} openMenu={openMenu} setOpenMenu={setOpenMenu} />
            <NavMenu id="company-menu" label="Company" links={companyLinks} openMenu={openMenu} setOpenMenu={setOpenMenu} />
            {selfServe && <Link href="/pricing" onClick={() => closeMenus()}>Pricing</Link>}
          </div>
          <div className="nav-actions">
            <NavMenu id="language-menu" label="English" icon={<GlobeIcon />} links={languageLinks} openMenu={openMenu} setOpenMenu={setOpenMenu} />
            {signedIn && !candidateCta ? (
              <Link href="/dashboard/agency" onClick={() => closeMenus()}>Dashboard</Link>
            ) : (
              <Link href={candidateCta ? candidatePracticeUrl(pathname) : SIGNIN_URL} onClick={() => closeMenus()}>Sign in</Link>
            )}
            {selfServe ? (
              <Link className="button button-light header-cta" href={START_FREE_URL} data-analytics-id="start_free" onClick={() => closeMenus()}>Start free</Link>
            ) : (
              <Link className="button button-light header-cta" href={candidateCta ? candidatePracticeUrl(pathname) : DEMO_URL} onClick={() => closeMenus()}>{candidateCta ? "Start practising" : "Book a demo"}</Link>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}

function MarketingFooter({ pathname }) {
  const candidateCta = useCandidateCta(pathname);
  const selfServe = isSelfServe(useVisitorRegion()) && !candidateCta;
  return (
    <footer className="site-footer">
      <div className="footer-inner">
        <div className="footer-cta-row">
          {candidateCta ? (
            <h2>Practise the interview<br />before it counts.</h2>
          ) : (
            <h2>See AIIA with a role<br />you are hiring for.</h2>
          )}
          {selfServe ? (
            <Link className="button button-light" href={START_FREE_URL} data-analytics-id="start_free">Start free</Link>
          ) : (
            <Link className="button button-light" href={candidateCta ? candidatePracticeUrl(pathname) : DEMO_URL}>
              {candidateCta ? "Start practising free" : "Book a demo"}
            </Link>
          )}
        </div>
        <div className="footer-divider"></div>
        <div className="footer-bottom">
          <Link className="wordmark footer-wordmark" href="/" aria-label="AIIA home">
            <MarketingLogo />
          </Link>
          <div className="footer-links">
            <div>
              <p>For hiring teams</p>
              <Link href="/recruiters">Recruiters</Link>
              <Link href="/recruiters/ai-interviewer">AI Interviewer</Link>
              <Link href="/recruiters/ai-video-interview-software">AI Video Interviews</Link>
              <Link href="/recruiters/interview-highlight-clips">Interview Highlight Clips</Link>
              <Link href="/recruiters/call-to-submittal">Call to Submittal</Link>
              <Link href="/recruiters/ai-resume-screening">Resume Screening</Link>
              <Link href="/recruiters/agentic-resume-search">Agentic Resume Search</Link>
              <Link href="/recruiters/career-page-builder">Career Pages</Link>
              <Link href="/recruiters/ai-interview-bot">AI Interview Bot</Link>
              <Link href="/recruiters/ai-interview-software">AI Interview Software</Link>
              <Link href="/recruiters/candidate-location-check">Candidate Location Check</Link>
              <Link href="/recruiters/joining-readiness">Joining Readiness</Link>
              <Link href="/recruiters/ai-phone-screening">Phone Screening, on request</Link>
            </div>
            <div>
              <p>Resources</p>
              <Link href="/blog">Blog</Link>
              <Link href="/interview-questions">Interview Questions</Link>
              <Link href="/compare">Compare</Link>
              <Link href="/industries">Industries</Link>
              <Link href="/integrations">Integrations</Link>
              <Link href="/tools">Tools</Link>
              <Link href="/connect">Connect</Link>
            </div>
            <div>
              <p>Company</p>
              <Link href="/about">About</Link>
              <Link href="/contact">Contact</Link>
              <Link href="/newsletter">Newsletter</Link>
              <Link href="/security">Security</Link>
              <Link href="/languages">Languages</Link>
              <Link href="/privacy-policy">Privacy Policy</Link>
              <Link href="/terms-of-use">Terms of Use</Link>
              <Link href="/refund-policy">Refund Policy</Link>
              <button type="button" onClick={openCookieSettings}>Cookie settings</button>
            </div>
            <div className="candidate-footer-group">
              <p>For candidates</p>
              <Link href="/candidates">AI Interview Preparation</Link>
              <Link href="/features/interview-warmup">Interview Warmup</Link>
              <Link href="/features/ai-mock-interview">AI Mock Interview</Link>
              <Link href="/features/ai-video-interview">Video Interview Practice</Link>
              <div className="footer-socials" aria-label="Social links">
                <a href="https://x.com/AinterviewAgent" target="_blank" rel="noopener noreferrer">X</a>
                <a href="https://www.instagram.com/aiinterviewagents_com?igsh=cmFscGZzODU2Zzhy" target="_blank" rel="noopener noreferrer">Instagram</a>
                <a href="https://www.linkedin.com/company/ai-interview-agent/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
              </div>
            </div>
          </div>
        </div>
        <nav className="footer-languages" aria-label="Language">
          <span aria-current="true">English</span>
          {NATIVE_LANDINGS.map((l) => (
            <Link key={l.code} href={l.path} lang={l.code} hrefLang={l.hreflang} data-analytics-id={`footer_language_${l.code}`}>{l.nativeName}</Link>
          ))}
        </nav>
        <p className="footer-note">AIIA helps hiring teams review candidates with structured evidence. AI Interview Agents is a product of DocSight AI. Support: <a href="mailto:contact@aiinterviewagents.com">contact@aiinterviewagents.com</a></p>
      </div>
    </footer>
  );
}

export default function MarketingShell({ children, notFound = false }) {
  const { pathname, hash } = useLocation();
  const previousPathnameRef = useRef(null);

  useEffect(() => {
    let animationFrame;
    let cancelled = false;
    let attempts = 0;
    const pathnameChanged = previousPathnameRef.current !== pathname;
    previousPathnameRef.current = pathname;

    if (pathnameChanged) window.scrollTo(0, 0);
    if (!hash) return undefined;

    let targetId;
    try {
      targetId = decodeURIComponent(hash.slice(1));
    } catch (_) {
      targetId = hash.slice(1);
    }

    const scrollToTarget = () => {
      if (cancelled) return;
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView();
        return;
      }
      attempts += 1;
      if (attempts < 120) animationFrame = window.requestAnimationFrame(scrollToTarget);
    };

    animationFrame = window.requestAnimationFrame(scrollToTarget);
    return () => {
      cancelled = true;
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, [pathname, hash]);

  // The native-language pages (/de, /fr, ..., and /<code>/features/<slug>) carry their own header and
  // footer in their language, so they get none of the English frame.
  if (!notFound && (NATIVE_BY_PATH[pathname.replace(/\/+$/, "")] || parseNativeCandidatePath(pathname))) {
    return <div className="aiia-site">{children}</div>;
  }

  return (
    // The not-found page takes the blog treatment at every address, the same
    // look as the blog's own "Article not found".
    <div className={`aiia-site ${notFound ? "marketing-blog" : routeClass(pathname)}`}>
      <a className="skip-link" href="#main">Skip to content</a>
      <MarketingHeader pathname={pathname} />
      <div className="marketing-content" id="main" role={MAIN_LANDMARK_ROUTES.has(pathname.replace(/\/+$/, "")) ? "main" : undefined} tabIndex={-1}>{children}</div>
      <MarketingFooter pathname={pathname} />
      <LeadSlideIn pathname={pathname} />
      <NativeLanguageBar pathname={pathname} />
    </div>
  );
}
